import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MessageSquare, Trash2, Edit2, User } from "lucide-react";
import { StarRating } from "./StarRating";

export const AveragesCard = ({ averages, reviewCount }) => {
  const { t } = useTranslation();
  return (
    <Card className="bg-card/50 border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t('reviews.averageRating')}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-bold gradient-text">{averages.overall}</span>
              <StarRating value={Math.round(averages.overall)} readonly size="sm" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('reviews.basedOn')} {reviewCount} {t('reviews.reviewsCount')}
            </p>
          </div>
          <div className="text-right text-sm space-y-1">
            {[
              { label: t('reviews.food'), value: averages.food },
              { label: t('reviews.service'), value: averages.service },
              { label: t('reviews.ambiance'), value: averages.ambiance },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-end gap-2">
                <span className="text-muted-foreground">{label}:</span>
                <span className="font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const UserReviewCard = ({ userReview, onEdit, onDelete }) => {
  const { t } = useTranslation();
  return (
    <Card className="bg-primary/5 border-primary/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>{t('reviews.yourReview')}</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-review-button">
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive" data-testid="delete-review-button">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex gap-4 mb-2 text-sm">
          {[
            { label: t('reviews.food'), value: userReview.food_rating },
            { label: t('reviews.service'), value: userReview.service_rating },
            { label: t('reviews.ambiance'), value: userReview.ambiance_rating },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center gap-1">
              <span className="text-muted-foreground">{label}:</span>
              <StarRating value={value} readonly size="sm" />
            </div>
          ))}
        </div>
        {userReview.comment && (
          <p className="text-sm text-muted-foreground italic">"{userReview.comment}"</p>
        )}
      </CardContent>
    </Card>
  );
};

export const ReviewList = ({ reviews, userId }) => {
  const { t } = useTranslation();
  const otherReviews = reviews.filter((r) => r.user_id !== userId);

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">
        {t('reviews.title')} ({reviews.length})
      </h4>

      {reviews.length === 0 ? (
        <Card className="bg-card/50 border-border">
          <CardContent className="py-8 text-center">
            <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">{t('reviews.noReviews')}</p>
            <p className="text-sm text-muted-foreground">{t('reviews.beFirst')}</p>
          </CardContent>
        </Card>
      ) : (
        otherReviews.map((review) => (
          <Card key={review.review_id} className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {review.user_picture ? (
                    <img src={review.user_picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <span className="font-medium text-sm">{review.user_name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-primary fill-primary" />
                  <span className="text-sm font-medium">
                    {((review.food_rating + review.service_rating + review.ambiance_rating) / 3).toFixed(1)}
                  </span>
                </div>
              </div>
              <div className="flex gap-3 text-xs text-muted-foreground mb-2">
                <span>{t('reviews.food')}: {review.food_rating}</span>
                <span>{t('reviews.service')}: {review.service_rating}</span>
                <span>{t('reviews.ambiance')}: {review.ambiance_rating}</span>
              </div>
              {review.comment && (
                <p className="text-sm text-muted-foreground">"{review.comment}"</p>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};
